import { User } from '../../../../../types';
import HtmlPreview from '../../../../shared/html-preview';
import { formatGroups, GroupsFormat } from './welcome-template-tokens';

interface PopulateWelcomeTemplateProps {
  user: User | undefined;
  template: string;
  height?: string;
  plainText?: boolean;
}

// // Define the type for keys of WelcomeTemplateFields
type TemplateField =
  | 'FirstName'
  | 'LastName'
  | 'Email'
  | 'Domain'
  | 'Password'
  | 'Groups';

const SAMPLE_GROUP_EMAILS = [
  'sales@example.com',
  'support@example.com',
  'all-staff@example.com',
];

const welcomeTemplateFields: Record<
  TemplateField,
  (user: User, groupsFormat: GroupsFormat) => string
> = {
  FirstName: (user) => user.name?.givenName || '',
  LastName: (user) => user.name?.familyName || '',
  Email: (user) => user.primaryEmail || '',
  Domain: (user) => user.primaryEmail.split('@')[1] || '',
  Password: (user) => user.password || '*******',
  Groups: (_user, groupsFormat) =>
    formatGroups(SAMPLE_GROUP_EMAILS, groupsFormat),
};

export default function PopulateWelcomeTemplate({
  user,
  template,
  height,
  plainText,
}: PopulateWelcomeTemplateProps) {
  const groupsFormat: GroupsFormat = plainText ? 'text' : 'html';

  function doCondReplacement(user: User | undefined, property: string) {
    const [beforeText, tag, afterText] = property.split('|');

    if (!tag) {
      return property;
    }

    const trimmedTag = tag.replace(/\|]/, '') as TemplateField;

    let replacement = '';

    if (user) {
      try {
        if (welcomeTemplateFields[trimmedTag]) {
          replacement = welcomeTemplateFields[trimmedTag](user, groupsFormat);
        }
      } catch {
        replacement = '';
      }
    }

    return beforeText + replacement + afterText;
  }

  function doPropReplacement(property: string, user: User | undefined) {
    const trimmedProperty = property as TemplateField;
    let replacement = '';

    if (user) {
      try {
        if (welcomeTemplateFields[trimmedProperty]) {
          replacement = welcomeTemplateFields[trimmedProperty](
            user,
            groupsFormat
          );
        }
      } catch {
        replacement = '';
      }
    }

    return replacement || '';
  }

  function populateEmailTemplate(template: string, user: User | undefined) {
    // Process [|] tags
    let characterStep = 0;

    while (characterStep < template.length) {
      if (
        template[characterStep] === '[' &&
        template[characterStep + 1] === '|'
      ) {
        const startChar = characterStep;
        let endChar = 0;
        let skipIterate = false;

        while (characterStep + 1 < template.length) {
          characterStep++;

          if (
            template[characterStep] === '|' &&
            template[characterStep + 1] === ']'
          ) {
            endChar = characterStep + 2;
            const tag = template.slice(startChar + 2, endChar - 2);

            if (!tag.includes('[|')) {
              const replacement = doCondReplacement(user, tag);

              if (replacement.trim() === '') {
                skipIterate = true;
              }

              template =
                template.slice(0, startChar) +
                replacement +
                template.slice(endChar);
              characterStep = startChar + replacement.length - 1;
            } else {
              characterStep = startChar;
            }

            break;
          }
        }

        if (!skipIterate) {
          characterStep = startChar + 1;
        } else {
          characterStep = startChar - 1;
        }
      } else {
        characterStep++;
      }
    }

    // Process {|} tags
    characterStep = 0;

    while (characterStep < template.length) {
      if (
        template[characterStep] === '{' &&
        template[characterStep + 1] === '|'
      ) {
        const startChar = characterStep;
        let endChar = 0;

        while (characterStep + 1 < template.length) {
          characterStep++;

          if (
            template[characterStep] === '|' &&
            template[characterStep + 1] === '}'
          ) {
            endChar = characterStep + 2;
            const tag = template.slice(startChar + 2, endChar - 2);
            const replacement = doPropReplacement(tag, user);

            template =
              template.slice(0, startChar) +
              replacement +
              template.slice(endChar);
            characterStep = startChar + replacement.length - 1;

            break;
          }
        }

        characterStep = startChar + 1;
      } else {
        characterStep++;
      }
    }

    return template;
  }

  return (
    <HtmlPreview
      html={populateEmailTemplate(template, user)}
      minHeight={100}
      height={height}
    />
  );
}
